import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Send, LoaderCircle, ShieldAlert } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = ["Primo colloquio", "Informazioni", "Terapia online", "Altro"];

const directContacts = [
  {
    icon: Phone,
    label: "Telefono",
    value: "+39 377 511 0949",
    href: "tel:+393775110949",
    testid: "direct-contact-phone-button",
  },
  {
    icon: Mail,
    label: "Email",
    value: "andrea.moretti.psicologo@outlook.it",
    href: "mailto:andrea.moretti.psicologo@outlook.it",
    testid: "direct-contact-email-button",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "Scrivimi direttamente",
    href: "https://wa.me/393775110949?text=Buongiorno%20Dott.%20Moretti%2C%20vorrei%20richiedere%20un%20primo%20colloquio.",
    testid: "direct-contact-whatsapp-button",
  },
];

export const Contatti = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "Primo colloquio", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        topic: form.topic,
        message: form.message.trim(),
      });
      toast.success("Messaggio inviato", {
        description: "Ti risponderò al più presto. Grazie per la fiducia.",
      });
      setForm({ name: "", email: "", phone: "", topic: "Primo colloquio", message: "" });
    } catch (err) {
      toast.error("Invio non riuscito", {
        description: "Riprova tra poco oppure contattami direttamente via telefono o WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-ink-soft border border-gold/15 focus:border-gold/60 rounded-xl px-5 py-4 text-sand placeholder:text-sand-faint outline-none transition-colors duration-300 text-sm sm:text-base";

  return (
    <section id="contatti" data-testid="contatti-section" className="py-24 sm:py-32 lg:py-40">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <Overline>Contatti</Overline>
          <h2 className="font-serif tracking-tight leading-snug text-3xl sm:text-4xl lg:text-5xl text-sand max-w-3xl">
            Il primo passo è <span className="italic text-gold">una conversazione</span>
          </h2>
          <p className="mt-7 max-w-2xl text-base sm:text-lg text-sand-muted leading-relaxed">
            Compila il modulo per richiedere un primo colloquio o scrivimi direttamente: ti
            risponderò personalmente. Ogni messaggio è accolto con riservatezza.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7" delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-ink-soft border border-gold/15 rounded-2xl p-8 sm:p-10 space-y-6"
            >
              <div className="flex flex-wrap gap-2" role="group" aria-label="Motivo del contatto">
                {topics.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`contact-topic-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, topic: t }))}
                    className={`text-xs sm:text-sm px-5 py-2.5 rounded-full border transition-all duration-300 ${
                      form.topic === t
                        ? "bg-gold text-ink border-gold font-semibold"
                        : "text-sand-muted border-gold/20 hover:border-gold/50"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-form-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Nome e cognome"
                  className={inputCls}
                />
                <input
                  data-testid="contact-form-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="La tua email"
                  className={inputCls}
                />
              </div>
              <input
                data-testid="contact-form-phone-input"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Telefono (facoltativo)"
                className={inputCls}
              />
              <textarea
                data-testid="contact-form-message-input"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Raccontami brevemente cosa ti porta qui…"
                className={`${inputCls} resize-y`}
              />
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <p className="text-xs text-sand-faint leading-relaxed max-w-sm">
                  Inviando accetti di essere ricontattato ai recapiti indicati. I tuoi dati non
                  saranno condivisi con terzi.
                </p>
                <button
                  data-testid="contact-form-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-2.5 text-sm font-semibold text-ink bg-gold hover:bg-sand px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
                >
                  {sending ? (
                    <>
                      Invio in corso <LoaderCircle size={16} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Invia la richiesta{" "}
                      <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col gap-5">
            {directContacts.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.label} delay={0.12 + i * 0.07}>
                  <a
                    data-testid={c.testid}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="group flex items-center gap-5 bg-ink-soft border border-gold/10 hover:border-gold/40 rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 shrink-0 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all duration-500">
                      <Icon size={20} className="text-gold group-hover:text-ink transition-colors duration-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.25em] text-sand-faint">{c.label}</p>
                      <p className="text-sand font-medium mt-1 truncate">{c.value}</p>
                    </div>
                  </a>
                </Reveal>
              );
            })}

            <Reveal delay={0.35}>
              <div className="flex items-start gap-4 border border-terracotta/30 bg-terracotta/10 rounded-2xl p-6" data-testid="emergency-disclaimer">
                <ShieldAlert size={20} className="text-terracotta shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-sand-muted leading-relaxed">
                  Questo sito non gestisce urgenze. In caso di emergenza o crisi acute contatta il{" "}
                  <span className="text-sand">112</span> o recati al pronto soccorso più vicino.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
